<script setup lang="ts">
import {Check, Link, Loader2, Pencil, Trash2, TriangleAlert} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import {BookingLinkPreset} from "@/core/http/resources/BookingLinkPresetResource";

export interface BookingLinkPresetRow {
    preset: BookingLinkPreset;
    /** « Qui · type » summary, resolved against the current team and catalog. */
    subtitle: string;
    /** Whether its type was hidden or deleted since, which suspends the link. */
    suspended: boolean;
}

defineProps<{
    rows: BookingLinkPresetRow[];
    selectedId: number | null;
    loading?: boolean;
    failed?: boolean;
}>();

const emits = defineEmits<{
    (e: 'select', preset: BookingLinkPreset): void;
    (e: 'edit', preset: BookingLinkPreset): void;
    (e: 'remove', preset: BookingLinkPreset): void;
}>();
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <p class="text-sm font-medium">
      Liens pré-établis
    </p>
    <div v-if="loading" class="flex items-center gap-2 py-3 text-sm text-gray-500">
      <Loader2 class="size-4 animate-spin" />
      Chargement des liens enregistrés…
    </div>
    <p v-else-if="failed" class="py-2 text-sm text-gray-500">
      Impossible de charger les liens enregistrés pour le moment. Vous pouvez
      toujours créer un lien à la volée dans l'onglet « Nouveau lien ».
    </p>
    <p v-else-if="!rows.length" class="py-2 text-sm text-gray-500">
      Aucun lien enregistré — créez-en un à la volée puis donnez-lui un nom. 🙂
    </p>
    <template v-else>
      <div
        v-for="row in rows"
        :key="row.preset.id"
        role="button"
        class="flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors"
        :class="selectedId === row.preset.id
          ? 'border-primary bg-primary-lightest'
          : 'border-slate-200 hover:border-slate-300'"
        @click="emits('select', row.preset)"
      >
        <span class="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary-lightest">
          <Link class="size-4 text-primary" />
        </span>
        <span class="min-w-0 flex-1">
          <span class="block truncate text-sm font-medium">{{ row.preset.name ?? 'Sans nom' }}</span>
          <span
            class="flex items-center gap-1 truncate text-xs"
            :class="row.suspended ? 'text-gray-dark' : 'text-gray-500'"
          >
            <TriangleAlert v-if="row.suspended" class="size-3 shrink-0 text-orange" />
            <span class="truncate">{{ row.subtitle }}</span>
          </span>
        </span>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          class="size-7"
          :aria-label="`Éditer ${row.preset.name ?? 'ce lien'}`"
          @click.stop="emits('edit', row.preset)"
        >
          <Pencil class="size-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          class="size-7 text-red-600 hover:text-red-700"
          :aria-label="`Supprimer ${row.preset.name ?? 'ce lien'}`"
          @click.stop="emits('remove', row.preset)"
        >
          <Trash2 class="size-3.5" />
        </Button>
        <Check
          class="size-4 shrink-0"
          :class="selectedId === row.preset.id ? 'text-primary' : 'text-transparent'"
        />
      </div>
    </template>
  </div>
</template>
